import { GraphQLClient } from 'graphql-request';
import type { ExecutionSummaryV2 } from '@flux/types';

import type * as GQL from './graphql/generated';
import { getSdk, type Sdk } from './graphql/generated';
import { graphQLEndpointFromEnv } from './graphqlEndpoint';

const API_URL = graphQLEndpointFromEnv();

/**
 * Public bootstrap mutations (OAuth callback, magic link) that don't require a session cookie.
 */
function getAnonymousSdk(): Sdk {
  const client = new GraphQLClient(API_URL, {
    headers: { 'content-type': 'application/json' },
  });
  return getSdk(client);
}

/**
 * Get a GraphQL client that carries the user's session cookie via browser credentials.
 * The browser automatically attaches cookies for same-origin or credentialed cross-origin requests.
 */
export function getClient(): GraphQLClient {
  return new GraphQLClient(API_URL, {
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  });
}

/**
 * Helper to get the authenticated SDK instance.
 */
function getApi(): Sdk {
  return getSdk(getClient());
}

export const api = {
  async listOrganizations(): Promise<GQL.ListOrganizationsQuery> {
    const sdk = getApi();
    return sdk.ListOrganizations();
  },

  async getGlobalStats(): Promise<GQL.GetGlobalStatsQuery> {
    const sdk = getApi();
    return sdk.GetGlobalStats();
  },

  async listProjects(): Promise<GQL.ListProjectsQuery> {
    const sdk = getApi();
    return sdk.ListProjects();
  },

  async getProject(id: string): Promise<GQL.GetProjectQuery> {
    const sdk = getApi();
    return sdk.GetProject({ id });
  },

  /**
   * Same GraphQL read path the dashboard uses (e.g. useProjectExecutions) — for REST routes and E2E API truth.
   */
  async listProjectExecutions(
    projectId: string,
    range: 'LAST_5_MIN' | 'LAST_1_HOUR' | 'LAST_24_HOURS' | 'LAST_30_DAYS',
    limit: number,
  ): Promise<ExecutionSummaryV2[]> {
    const client = getClient();
    const document = `
      query ListProjectExecutionsForApi($id: ID!, $range: TimeRange!, $limit: Int!) {
        project(id: $id) {
          executions(range: $range, limit: $limit) {
            executionId
            projectId
            receivedAt
            startedAt
            completedAt
            durationMs
            status
            entry
            eventCount
            requestId
            parentRequestId
            traceId
            preview { kind label }
            meta
            error { name message stack }
          }
        }
      }
    `;
    const data = await client.request<{
      project: { executions: ExecutionSummaryV2[] } | null;
    }>(document, { id: projectId, range, limit });
    return data.project?.executions ?? [];
  },

  async acknowledgeAlert(id: string): Promise<GQL.AcknowledgeAlertMutation> {
    const sdk = getApi();
    return sdk.AcknowledgeAlert({ id });
  },

  async createAlertRule(args: {
    orgId: string;
    projectId?: string | undefined;
    ruleType: string;
    threshold: number;
    windowMinutes: number;
    notifyEmails?: string[] | undefined;
  }): Promise<GQL.CreateAlertRuleMutation> {
    const sdk = getApi();
    return sdk.CreateAlertRule(args);
  },

  async getProfile(): Promise<GQL.GetProfileQuery> {
    const sdk = getApi();
    return sdk.GetProfile();
  },

  async upsertUser(args: {
    email: string;
    name?: string | undefined;
    image?: string | undefined;
    providerId?: string | undefined;
    providerAccountId?: string | undefined;
  }): Promise<GQL.UpsertUserMutation> {
    return getAnonymousSdk().UpsertUser(args);
  },

  async verifyMagicLink(email: string, token: string): Promise<GQL.VerifyMagicLinkMutation> {
    return getAnonymousSdk().VerifyMagicLink({ email, token });
  },

  async initiateVerification(email: string): Promise<GQL.InitiateVerificationMutation> {
    return getAnonymousSdk().InitiateVerification({ email });
  },

  async createProject(name: string, orgId: string): Promise<GQL.CreateProjectMutation> {
    const sdk = getApi();
    return sdk.CreateProject({ name, orgId });
  },

  async updateProject(
    id: string,
    updates: { name?: string | undefined; agentUrl?: string | undefined },
  ): Promise<GQL.UpdateProjectMutation> {
    const sdk = getApi();
    return sdk.UpdateProject({ id, ...updates });
  },

  async rotateSecret(id: string): Promise<GQL.RotateSecretMutation> {
    const sdk = getApi();
    return sdk.RotateSecret({ id });
  },

  async getUserByEmail(email: string): Promise<GQL.GetUserByEmailQuery> {
    const sdk = getApi();
    return sdk.GetUserByEmail({ email });
  },

  async getUsersByIds(ids: string[]): Promise<GQL.GetUsersByIdsQuery> {
    const sdk = getApi();
    return sdk.GetUsersByIds({ ids });
  },

  async getOrgMemberships(): Promise<GQL.GetOrgMembershipsQuery> {
    const sdk = getApi();
    return sdk.GetOrgMemberships();
  },

  async getOrganization(id: string): Promise<GQL.GetOrganizationQuery> {
    const sdk = getApi();
    return sdk.GetOrganization({ id });
  },

  async getOrgMembers(orgId: string): Promise<GQL.GetOrgMembersQuery> {
    const sdk = getApi();
    return sdk.GetOrgMembers({ orgId });
  },

  async getProjectMembers(projectId: string): Promise<GQL.GetProjectMembersQuery> {
    const sdk = getApi();
    return sdk.GetProjectMembers({ projectId });
  },

  async getProjectToken(id: string): Promise<GQL.GetProjectTokenQuery> {
    const sdk = getApi();
    return sdk.GetProjectToken({ id });
  },

  async getSubscription(): Promise<GQL.GetSubscriptionQuery> {
    const sdk = getApi();
    return sdk.GetSubscription();
  },

  async getAlertEvents(projectIds: string[]): Promise<GQL.GetAlertEventsQuery> {
    const sdk = getApi();
    return sdk.GetAlertEvents({ projectIds });
  },

  async getAlertRules(orgId: string): Promise<GQL.GetAlertRulesQuery> {
    const sdk = getApi();
    return sdk.GetAlertRules({ orgId });
  },

  async getTestCases(projectId: string): Promise<GQL.GetTestCasesQuery> {
    const sdk = getApi();
    return sdk.GetTestCases({ projectId });
  },

  async createOrganization(name: string, slug?: string): Promise<GQL.CreateOrganizationMutation> {
    const sdk = getApi();
    return sdk.CreateOrganization({ name, slug });
  },

  async updateOrganization(
    id: string,
    updates: { name?: string | undefined; slug?: string | undefined },
  ): Promise<GQL.UpdateOrganizationMutation> {
    const sdk = getApi();
    return sdk.UpdateOrganization({ id, ...updates });
  },

  async deleteOrganization(id: string): Promise<GQL.DeleteOrganizationMutation> {
    const sdk = getApi();
    return sdk.DeleteOrganization({ id });
  },

  async upsertOrgMember(args: {
    orgId: string;
    userId: string;
    role: string;
    invitedBy: string;
  }): Promise<GQL.UpsertOrgMemberMutation> {
    const sdk = getApi();
    return sdk.UpsertOrgMember(args);
  },

  async updateOrgMemberRole(
    orgId: string,
    userId: string,
    role: string,
  ): Promise<GQL.UpdateOrgMemberRoleMutation> {
    const sdk = getApi();
    return sdk.UpdateOrgMemberRole({ orgId, userId, role });
  },

  async removeOrgMember(orgId: string, userId: string): Promise<GQL.RemoveOrgMemberMutation> {
    const sdk = getApi();
    return sdk.RemoveOrgMember({ orgId, userId });
  },

  async softDeleteProject(id: string): Promise<GQL.SoftDeleteProjectMutation> {
    const sdk = getApi();
    return sdk.SoftDeleteProject({ id });
  },

  async updateProjectAgentUrl(
    id: string,
    agentUrl?: string,
  ): Promise<GQL.UpdateProjectAgentUrlMutation> {
    const sdk = getApi();
    return sdk.UpdateProjectAgentUrl({ id, agentUrl });
  },

  async insertRevocation(args: {
    projectId: string;
    type: string;
    jti?: string | undefined;
  }): Promise<GQL.InsertRevocationMutation> {
    const sdk = getApi();
    return sdk.InsertRevocation(args);
  },

  async upsertProjectMember(args: {
    projectId: string;
    userId: string;
    role: string;
    addedBy: string;
  }): Promise<GQL.UpsertProjectMemberMutation> {
    const sdk = getApi();
    return sdk.UpsertProjectMember(args);
  },

  async removeProjectMember(
    projectId: string,
    userId: string,
  ): Promise<GQL.RemoveProjectMemberMutation> {
    const sdk = getApi();
    return sdk.RemoveProjectMember({ projectId, userId });
  },

  async deleteAlertRule(id: string, orgId: string): Promise<GQL.DeleteAlertRuleMutation> {
    const sdk = getApi();
    return sdk.DeleteAlertRule({ id, orgId });
  },

  async upsertTestCase(
    projectId: string,
    input: GQL.UpsertTestCaseInput,
  ): Promise<GQL.UpsertTestCaseMutation> {
    const sdk = getApi();
    return sdk.UpsertTestCase({ projectId, input });
  },

  async updateTestCase(args: {
    id: string;
    projectId: string;
    name?: string | undefined;
    lastStatus?: string | undefined;
  }): Promise<GQL.UpdateTestCaseMutation> {
    const sdk = getApi();
    return sdk.UpdateTestCase(args);
  },

  async deleteTestCase(id: string, projectId: string): Promise<GQL.DeleteTestCaseMutation> {
    const sdk = getApi();
    return sdk.DeleteTestCase({ id, projectId });
  },

  async updateTestCaseStatus(
    id: string,
    status: string,
  ): Promise<GQL.UpdateTestCaseStatusMutation> {
    const sdk = getApi();
    return sdk.UpdateTestCaseStatus({ id, status });
  },

  async syncSubscription(subscriptionId: string): Promise<GQL.SyncSubscriptionMutation> {
    const sdk = getApi();
    return sdk.SyncSubscription({ subscriptionId });
  },

  async checkout(args: {
    planId: string;
    productId: string;
    returnUrl: string;
    countryCode: string;
    name?: string | undefined;
  }): Promise<GQL.CheckoutMutation> {
    const sdk = getApi();
    return sdk.Checkout(args);
  },

  async billingPortalUrl(email: string, returnUrl: string): Promise<GQL.BillingPortalUrlMutation> {
    const sdk = getApi();
    return sdk.BillingPortalUrl({ email, returnUrl });
  },

  async processExecutionMetrics(
    record: Record<string, unknown>,
  ): Promise<GQL.ProcessExecutionMetricsMutation> {
    const sdk = getApi();
    return sdk.ProcessExecutionMetrics({ record });
  },
};
